import { Entity } from 'src/app/core/data/schema/entity';
import { PageBlock } from './page-block';
import { PageAccessMapping } from './page-access-mappings';
import { PageState } from 'src/app/core/base-page';
import { SelectOption } from 'src/app/core/component/form-inputs/select-input/select-input.component';
import { Utilities } from 'src/app/core/helper/util';
import { SafeResourceUrl } from '@angular/platform-browser';
import { RoutingHelper } from 'src/app/shared/helper/routing.helper';
import { Category } from './category';
import { Product } from '../ecommerce/product';
import { DateTime } from 'luxon';
import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { CbsEvent } from '../events/event';
import { FundraisingCampaign } from '../fundraising/fundraising-campaign';
import { Sponsor } from '../fundraising/sponsor';
import { TimeSeriesData } from '../reporting/time-series-data';
import { FileUpload } from './file-upload';
import { MediaConfig } from 'src/app/modules/page-builder/page/entity-media/entity-media.component';
import { CategoryGroup } from './category-group';
import { Membership } from '../memberships/membership';
import { AppSettings } from 'src/app/core/configs/app.settings';

/**
 * Page Types are used to identify what type of entity is linked to this page 
 * and also to differentiate where pages are listed on the dashboard, like 
 * should a page show up under Product categories or Event categories.
 * 
 * Many Pages are linked to entities, and when linked, we are talking about
 * a specific entity and the Page (usually) represents an actual detail
 * page view. Usually because some, like documents and directories, don't have 
 * detail pages. 
 * 
 * Specifically, different types of items may be rendered in a unique way, though 
 * some might be rendered very much the same, i.e. when using PageBlocks. That is,
 * the structure of how it is rendred might be the same, but the content will
 * be different.
 * 
 * For example, there will likely be no difference between how a Product and 
 * a Donation is rendered, since Donations are just products with type of donation.
 * But best to keep them logically separated here in case some unique rendering
 * requirements eventually emerge.
 * 
 * Similarly, Posts, Events, Donations, Sponsors and other items could very well just 
 * be rendered the same as each other or any Page for that matter. 
 * The main difference is that the specific items might have a custom way of
 * rendering the first 'block', which wouldn't use a 'PageBlock' record at all,
 * but instead have a specific rendering component that knows how to use the info
 * off the specific item object.
 */
export enum PageType {
    // the first bunch of types do NOT map to a specific entity,
    // so all the info to render exists on the page and its own blocks only.
    Page = 1,           // static page - comes from just the Pages table 
    Post = 2,           // news article - comes from just the Pages table 

    // Each Category will have it's own page, routed to under the corresponding
    // CategoryGroup, further below
    MarketplaceCategory = 3,
    EventCategory = 4,
    AuctionCategory = 5,
    SponsorCategory = 6,
    FilterCategory = 7,
    ResourceCategory = 29,
    MembershipCategory = 31,
    PostCategory = 33,

    // These are CategoryGroup pages, of which there really only exists one page each
    // excpet for the Filter Home, since admins can create several of these 
    MarketplaceHome = 8,
    EventsHome = 9,
    SponsorsHome = 10,
    AuctionHome = 11,
    FilterHome = 12, // this one will 
    ResourcesHome = 13,
    MembershipHome = 30,
    PostHome = 32,


    // These page types are all linked to entities
    Resource = 14,      // comes from CategoryGroups Table 
    Donation = 15,      // comes from Products table 
    Event = 16,         // comes from Events table
    ForumCategory = 17, // comes from ForumCategoires table (when we implement)
    FundraisingCampaign = 18,// comes from FundraisingCampaigns table
    Membership = 19,    // comes from Memberships table TODO
    Newsletter = 20,    // comes fron Newsletters table
    Onboarding = 21,    // comes from Onboarding table
    Product = 22,       // comes from Products table
    Special = 23,       // comes from Specials table
    Sponsor = 24,       // comes from Products table
    Survey = 25,        // comes from Products table
    Raffle = 26,        // comes from Raffles table
    Auction = 27,        // comes from Auctions table
    Directory = 28,     // comes from CategoryGroups Table 
    // 29 is resource category above


    // PSEUDO PAGES
    // these are pages that are actually appended or prepended to other pages,
    // and are never displayed on their own. Like they shouldn't even really
    // have a slug. 

    // footer to be appended at bottom of every page in site
    Footer = 100,

    // affiliate pages are used for customizing donation pages, or really any page 
    // that can have sharing attributed back to a user
    Affiliate = 101,
}

export class PageSortOrder {
    pageId: number;
    sortOrder: number;
}


export class PageTypeSelectOptions {

    static _allsorted: SelectOption[];
    static get allSorted() {
        if (PageTypeSelectOptions._allsorted == null) {
            PageTypeSelectOptions._allsorted = PageTypeSelectOptions.all.sort((a, b) => a.displayValue.localeCompare(b.displayValue));
        }
        return PageTypeSelectOptions._allsorted;
    }

    static async loadChildItems(pgType: PageType, valueProperty: string = 'pageId'): Promise<SelectOption[]> {
        const pagesForType = await Page.fetchForPageType(pgType);
        let childItems: SelectOption[] = [];
        pagesForType.forEach(p => {
            // only include active
            if ((p.closeAtDate == null || !p.closeAtDate.isValid || p.closeAtDate > DateTime.now()) && p.pgStatus == PageStatus.Live) {
                childItems.push({ value: p[valueProperty], displayValue: p.title });
            }
        })
        childItems.sort((a, b) => a.displayValue.localeCompare(b.displayValue));
        return childItems
    }

    static all: SelectOption[] = [
        { value: PageType.Page, displayValue: 'Page' },
        { value: PageType.Post, displayValue: 'News' },
        { value: PageType.MarketplaceCategory, displayValue: 'Marketplace Categories' },
        { value: 4, displayValue: 'Event Categories' },
        // {value: 5, displayValue: 'Auction Categories'}, Needs more integration
        { value: 6, displayValue: 'Sponsor Categories' },
        { value: 7, displayValue: 'Filter Categories' },


        { value: 8, displayValue: 'Marketplace Home' },
        { value: 9, displayValue: 'Events Home' },
        { value: 10, displayValue: 'Sponsors Home' },
        // {value: 11, displayValue: 'Auctions Home'},
        { value: 12, displayValue: 'Custom Filter Home' },

        { value: 13, displayValue: 'Resources Home' },
        { value: 14, displayValue: 'Resource' },
        { value: 15, displayValue: 'Donation' },
        { value: 16, displayValue: 'Event' },
        { value: 17, displayValue: 'Forum Categories' },
        { value: 18, displayValue: 'Fundraising Campaign' },
        { value: 19, displayValue: 'Membership' },
        // { value: 21, displayValue: 'Onboarding' },
        { value: 22, displayValue: 'Product' },
        { value: 23, displayValue: 'Special' },
        { value: 24, displayValue: 'Sponsor' },
        { value: 25, displayValue: 'Survey' },
        { value: 26, displayValue: 'Raffle' },
        { value: 27, displayValue: 'Auction' },
        // {value: 28, displayValue: 'Directory'},
        { value: PageType.ResourceCategory, displayValue: 'Resource Categories' },
        { value: PageType.MembershipHome, displayValue: 'Membership Home' },
        { value: PageType.MembershipCategory, displayValue: 'Membership Categories' },
        { value: PageType.PostHome, displayValue: 'News Home' },
        { value: PageType.PostCategory, displayValue: 'News Categories' },
    ];
}

/**
 * It's possible that a page is LIVE but openAt is in the future
 * or closeAt is in the past, in which case we should show a different
 * status, perhaps Future and Past? though we wouldn't update DB 
 * status to that value..
 */
export enum PageStatus {
    Draft = 1,
    Live = 2,
    Archived = 3
}

export enum MediaDisplay {
    NoMedia = 1,
    HalfWidth = 2,
    FullWidth = 3,
    CustomBlocksOnly = 4,
}

/**
 * Pages are used pretty ubiquitously through the front end of 
 * the app. There are static pages, news posts, newsletters,
 * sponsors, events, products, categories, etc.
 * 
 * Pages have some critical information on how any of these elements
 * are displayed, like the title, short descriptiona and featured 
 * image are used for social sharing previews.
 * 
 * Status, openAt, closeAt, hideUntilOpen and tags determine
 * if the page is visible to the user (in addition to access 
 * restrictions, which are stored in separate table).
 * 
 * Views lets us collect basic impression metrics across all entities.
 */
export class Page extends Entity {

    protected static className = 'Page';
    protected static idField = 'pageId';

    public static pageStates: string[] = ['All', 'Visible', 'Scheduled', 'Draft', 'Archived'];

    pageId: number = 0;
    slug: string; // alternate url path, so slug needs to be unique. If not set, use id.
    communityId: number;
    auctionId: number;
    parentPageId: number;
    pgType: PageType;
    pgItemId: number; // the primary key of item (event, category, product, sponsor, etc) that this page belongs to
    pgStatus: PageStatus = PageStatus.Draft;
    title: string;
    shortDesc: string;
    featureImgPath: string;
    body: string;
    imagePaths: string;
    sendNotification: boolean = false;
    lastNotificationSent: string;

    // overloading this to also be used for external url if page is 
    // specified as external link page wrapper
    videoUrl: string;

    useVid: boolean = false;
    allowCrawling: boolean = true;
    hideUntilOpen: boolean;
    showSubcatTiles: boolean = false;
    numSubcatItems: number;
    mediaDisplay: MediaDisplay = MediaDisplay.FullWidth;
    bannerMaxH: string = '60vh';
    bannerRatio: string = 'calc(100vw / 2 * 1)';
    imgAnchor: string = 'center';
    config: string;
    views: number;
    sortOrder: number;
    createdByName: string;
    notFiltered: boolean = false;
    pinned: boolean = false;
    showPublishDate: boolean = true;

    filePath: string;
    fileName: string;
    fileDownloads: number;

    // no page builder, just title, short desc, feature image for card display, click opens url
    isExternalLink: boolean;

    // these are dates passed down as UTC strings from API, and they
    // need to be set with any modified values as UTC string before saving back up 
    openAt: string;
    closeAt: string;
    closeAtDisplay: string;

    created: string;

    deleted: boolean = false;

    // these are Luxon DateTime objects that we should use for all manipulation
    openAtDate: DateTime;
    closeAtDate: DateTime;
    createdDate: DateTime;

    publishDateString: string; // for display in table
    publishedAtFormatted: string = '-'; // for display in table

    // list of page blocks populated by API 
    pageBlocks: PageBlock[];

    // Entity set by api corresponding to page typ
    product: Product;
    event: CbsEvent;
    categoryGroup: CategoryGroup;
    sponsor: Sponsor;
    fundraisingCampaign: FundraisingCampaign;
    membership: Membership;

    // list of item page stubs populated by API 
    subItems: Page[] = [];
    subCategories: Category[] = [];

    // all category ids that this page has been mapped to, including system categories
    // like event and product categories, but also custom filter group categories like locations
    mappedCategories: number[] = [];
    categoryNames: string;

    // not actually used, but need to clear on save
    fileUploads: FileUpload[] = [];

    // normalized to first in mapping 
    fundraiserId: number;

    // pages don't have a survey ID, but 
    // to satisfy the visibility component, we need to 
    // define this here. Anything that actually uses it
    // will have an overridden survey ID field in the class
    surveyId: number;


    // fields in client, not used by API
    sponsorAmount: number;
    instanceId: number;

    // helper to get list of possible item type options for select inputs
    get pageTypeOptions() { return PageTypeSelectOptions.allSorted; }

    sanitizedFeatureImgUrl: SafeResourceUrl;

    // populated on fundraiser overview page with amount raised for that campaign from this page
    raised: number = 0;

    // using getter here cause i don't want this to impact change checking
    get sanitizedBody() {
        if (this.body != null) { return ServiceHelper.sanitizer.bypassSecurityTrustHtml(this.body) }
        else { return null }
    };
    set sanitizedBody(value: any) {
        // do nothing, this just satisfies the setter requirement when deserializing
    }

    private _cfgObj: MediaConfig;
    get cfgObj() { return this._cfgObj };
    set cfgObj(v: any) { this._cfgObj = v }

    // similar to status, but considers publish date
    state: string;

    constructor(page?: Partial<Page>) {
        super();
        Object.assign(this, page);
        this.init();
    }

    get hasOpened() { return this.openAtDate != null && this.openAtDate < DateTime.local() }
    get hasClosed() { return this.closeAtDate != null && this.closeAtDate < DateTime.local() }


    init() {
        // handle bad data, likely just from early dev
        if (this.openAt != null && this.openAt.startsWith('0001')) this.openAt = null;
        if (this.closeAt != null && this.closeAt.startsWith('0001')) this.closeAt = null;

        this.closeAtDate = DateTime.fromISO(this.closeAt, { zone: 'utc' }).toLocal();
        this.createdDate = DateTime.fromISO(this.created, { zone: 'utc' }).toLocal();

        // udpate datetime strings to be input compatible, BUT WE NEED TO CONVERT BACK TO UTC BEFORE SAVE!
        this.closeAt = this.closeAtDate.toISO();
        this.closeAtDisplay = this.closeAtDate.toFormat('h:mm a, MMM dd yyyy');

        if (this.openAt != null) {
            this.openAtDate = DateTime.fromISO(this.openAt, { zone: 'utc' });
            this.openAt = this.openAtDate.toISO();
            this.publishedAtFormatted = DateTime.fromISO(this.openAt).toFormat('MMM-dd-yyyy h:mm a');
        }

        if (this.pgStatus != PageStatus.Draft && this.openAtDate != null) {
            this.publishDateString = this.openAtDate.toFormat('MMMM d, yyyy');
        } else {
            this.publishDateString = "-";
        }

        this.setState();

        if (this.categoryNames == null || this.categoryNames.length == 0) {
            this.categoryNames = 'Uncategorized';
        }

        // load in the config object so change detection works
        if (this.config != null) {
            this.cfgObj = JSON.parse(this.config);
        } else {
            this.cfgObj = new MediaConfig();
        }
    }

    setState() {
        switch (this.pgStatus) {
            case PageStatus.Draft: this.state = 'Draft'; break;
            case PageStatus.Archived: this.state = 'Archived'; break;
            default:
                if (this.openAtDate != null && this.openAtDate > DateTime.local()) {
                    this.state = 'Scheduled';
                } else if (this.closeAtDate != null && this.closeAtDate < DateTime.local()) {
                    this.state = 'Archived';
                } else {
                    this.state = 'Visible';
                }
        }
    }

    // ['Visible, Draft', 'Visible', 'Scheduled', 'Archived', 'Draft', 'All'];
    matches(selected: string) {
        if (selected == 'all') {
            return true;
        }
        if (selected == 'non-archived') {
            return ['draft', 'scheduled', 'visible'].includes(this.state.toLowerCase());
        }
        return selected == this.state.toLowerCase()
    }

    hasChanges() {
        return Page.hasChanges(Page);
    }

    revert() {
        return Page.revert(Page);
    }

    // overriding save function so we for sure always set dates back to UTC
    override async save(busyState: PageState = null, controller: string | undefined = this.controller, endpoint: string = ''): Promise<boolean> {

        if (this.openAt != null) { this.openAt = DateTime.fromISO(this.openAt).toUTC().toISO(); }
        else { this.openAt = null }
        if (this.closeAt != null) { this.closeAt = DateTime.fromISO(this.closeAt).toUTC().toISO(); }
        else { this.closeAt = null }

        this.fileUploads = undefined;


        // all pages have media tab which now uses a config to store addtional
        // media block configuration settings, and we need to stringify it to save
        // it. Better to do here, otherwise we get 'unsaved' changes even if nothing
        // changed
        if (this.cfgObj != null) {
            this.config = JSON.stringify(this.cfgObj);
        }

        if (await super.save(busyState, controller, endpoint)) {
            this.init();
            return true; ''
        }
        return false;
    }

    public async copyPage(busyState?: PageState) {
        busyState.busy = true;
        const copy = new Page(await Entity.factory.post("Copy", { name: this.title + ' - Copy' } as any, 'pages', `copy/${this.pageId}`));
        busyState.busy = false;
        return copy;
    }

    async addImg(base64Imgs: string) {
        if (Utilities.stringEmpty(this.imagePaths)) {
            this.imagePaths = base64Imgs;
        } else {
            this.imagePaths = this.imagePaths + '|' + base64Imgs;
        }
    }

    async savePageBlock(busyState: PageState, block: PageBlock): Promise<boolean> {
        block.pageId = this.pageId;
        return await block.save(busyState);
    }

    async fetchPageBlocks(): Promise<PageBlock[]> {
        return await PageBlock.fetchChildrenFor(PageBlock, this.pageId, null, `forPage/${this.pageId}`);
    }

    async removePageBlock(busyState: PageState, block: PageBlock): Promise<boolean> {
        //return await block.delete(busyState, `forPage/${this.pageId}`);
        return await block.delete(busyState);
    }

    async saveAccessMappings(busyState: PageState, access: PageAccessMapping): Promise<boolean> {
        return await access.save(busyState, null, `forPage/${this.pageId}`);
    }

    async fetchAccessMappings(): Promise<PageAccessMapping[]> {
        return await PageAccessMapping.fetchChildrenFor(PageAccessMapping, this.pageId, null, `forPage/${this.pageId}`);
    }

    // this function is called when someone clicks on an item preview. It will check the item type
    // on the page and open the route accordingly
    viewItem() {
        RoutingHelper.routeTo(this.pgType, this.slug, this.pgItemId);
    }

    // ----------------------------------------------------
    // Service-like features from here on down
    // ----------------------------------------------------

    /** 
     * Something like this should be used in the client across
     * most entities, to avoid repeated loading of data
         
        // caching so we only fetch any entity set from server once
        static cachedPages: Page[];   
        static get allLoaded() { return this.cachedPages != null} 

        static async loadAll(): Promise<Page[]> {
            if ( Page.cachedPages == null ) {
                Page.cachedPages = await Page.fetch(Page) as Page[];
            }
            return Page.cachedPages;
        }
    */

    // keep local cache of last accessed object, both a copy that is never modified unless saved,
    // and a 'working copy' that is edited in screens.
    protected static _cachedServerObj: Page; // unmodified until saved
    protected static _cachedObj: Page; // working copy
    protected static _cacheExpiry: number;
    // protected static _rawType: typeof RawPage = RawPage;

    public static clearCache() {
        Page._cachedObj = undefined;
        Page._cachedServerObj = undefined;
    }

    public static async fetchForPageType(type: PageType): Promise<Page[]> {
        // FIXME - once we have API, we'll want to fetch like this
        return await Page.fetchArray(Page, 'pages', `forType/${type}`);
    }

    public static async load(id: number) {
        return await Page.getById(Page, id);
    }

    public static async fetchMenuPages() {
        return await Page.fetchArray(Page, 'pages', `forMenus`);
    }

    public static async getBySlug(slug: string) {
        return await Page.getObject(Page, 'pages', `forslug/${slug}`, true);
    }

    public static async getClientPageById(pageId: number) {
        return await Page.getObject(Page, 'pages', `${pageId}/client`, true);
    }

    public static async getItemId(pageId: number, type: PageType) {
        return await Page.fetchUntyped('Loading Details', 'pages', `getitemid/${pageId}/${type}`, 'text');
    }

    public static async fetchViews(pageId: number): Promise<TimeSeriesData[]> {
        //const resp =  <PageView[]>(await super.fetchArray(PageView, 'pages', `views/${pageId}`) as PageView[]);
        const resp = await super.fetchUntyped('Views', 'pages', `views/${pageId}`, 'json') as TimeSeriesData[];
        return resp;
    }

    static async updateSortOrder(sortedItems: PageSortOrder[]) {
        await ServiceHelper.httpSvc.put(sortedItems, `${AppSettings.apiURL}/pages/sortorder`, 'json');
    }
}